/**
 * 小助理 —— 当面对话浮层的宿主（复用通用 VnDialog）+ 功能菜单 + 功能弹窗的挂载点。
 *
 * 语义：消息产生即弹，一次积压合成一段（逐句点继续），读完整段自动收起；
 * 没有未读红点、没有跳过、没有翻页。月度执行弹窗接管屏幕时整段停下、不记已读，
 * 跑完自动连播。自动播报播完就收，不顺势递菜单（用户 09-29 实测：那样等于「点完消息之后还有三个选项」，
 * 菜单只该出现在玩家主动点入口的时候）。
 *
 * 这张纸卡是**单主人**的（2026-11-02 决策页软锁整改）：外部宿主借用它（送达链、决策页剧情演出档）
 * 也走同一条队列，一次只放一段，后到的排队。VnDialog 只有一个「说完交给谁」的位置，
 * 插队会让正在播那段的收口回调永远不触发，玩家点「确认行程」后月度弹窗开不出来。
 *
 * 悬浮入口＝**叫小助理过来办事**（第七十三批改判，⛔ 旧「搭两句闲话」的闲聊分支已删）：
 * 只有玩家主动点入口才给功能菜单（纸卡末句的分支选项，复用 VnDialog 现成的 choices + onPick）；
 * 点的时候若还有积压，先播完积压再给菜单 ⇒ 入口永远可达。
 * 菜单项见下方 `MENU`（数组顺序＝展示顺序）。⚠️ 单子本身是**动态**的：造型工作室在咖位跨过 Lv.3 之前
 * 压根不出现在清单里（10-02 用户令，判据见 `visibleMenu`），所以实测的项数会随进度从三项变四项。
 */
import { EventBus, GameEvents } from '../../core/EventBus';
import { assistantSystem } from '../../systems/AssistantSystem';
import { reviewSystem } from '../../systems/ReviewSystem';
import { ASSISTANT, assistantAvatarPngSrc } from '../../data/Assistant';
import { DialogChoice, DialogLine, DialogSpeaker, VnDialog } from '../VnDialog';
import { showHint } from '../ui';
import { CareerRecordPanel } from './CareerRecordPanel';
import { EndorseListPanel } from './EndorseListPanel';
import { ReviewPanel } from './ReviewPanel';

const SPEAKER: DialogSpeaker = {
    id: ASSISTANT.id,
    name: ASSISTANT.name,
};

// 小助理头像 = 捏脸渲染（异步出图填进 speaker；VN 打开前基本已就绪，未就绪时无头像帧一闪而过）
void assistantAvatarPngSrc().then(src => { SPEAKER.avatarSrc = src; });

/** 功能菜单项（数组顺序＝展示顺序；⛔ 给未实装项另配徽章，它们与已实装项同款纯文字）
 *  `reply`＝点了不当场开面板，而是让小助理**原位回一句**（业内资讯未上线期间的默认答复，10-06 用户令） */
const MENU: { key: string; label: string; reply?: string }[] = [
    { key: 'career', label: '查看实绩' },
    { key: 'endorse', label: '在约商务' },
    { key: 'studio', label: '造型间' },
    { key: 'reviews', label: '网友评价' },
    { key: 'news', label: '业内资讯', reply: ASSISTANT.newsReply },
];

/**
 * 本次开菜单**实际可见**的那几项（2026-10-02 用户令「工作室没开放就⛔ 显示这个选项，开放了再显示」）。
 * ⇒ 门控从「点了给一句门槛话」前移成「压根不出现在单子上」： Lv.3 之前单子上就三项，
 * ⚠️ 调用方必须**摇一次存下来**（`this.menuItems`）：`VnDialog.onPick` 回传的是**当时传给它的那份 `choices` 数组的下标**
 * （与卡片上的展示顺序无关），清单被过滤过就⛔ 拿它回查静态 `MENU`（少一项⇒整体串位，点第二项读出第三项的键）。
 *
 * 10-06 起两种「点了⛔ 开功能」的形制在此分家：**造型工作室**未解锁⇒这项压根⛔ 出现（上面那条令，隐藏形制）；
 * **网友评价 Lv.1**⇒项照列、就地挂一句 `reply` 让小助理回话（用户令「Lv1 直接不显示网友评价，
 * 小助理回复【xx姐，网上还搜不到你的名字呢，继续努力吧！】」）——⛔ 隐藏、⛔ 开面板，只回话。
 */
function visibleMenu(): { key: string; label: string; reply?: string }[] {
    const reviewsLocked = !reviewSystem.unlocked();
    // 自由出行批（2026-10-11）：原 Lv.3 解锁门取消（造型间开局即有；买断制商店在周末商业街，
    // 价签本身是门槛），⛔ 再按 stylingSystem.isUnlocked() 过滤
    return MENU
        .map(m => (m.key === 'reviews' && reviewsLocked && !m.reply)
            ? { ...m, reply: assistantSystem.joinTerm(ASSISTANT.noReviewsReply) }
            : m);
}

/** 待实装项的回应（占位文案，人设定稿后随 `data/Assistant.ts` 一起改） */
const TODO_HINT = '这个还没做好，过两天再叫我～';

/**
 * 队列里的一段（2026-11-02 决策页软锁整改：这张卡一次只放一段，后到的排队）。
 *  - `backlog`＝小助理积压播报，开口那一刻才现取未读区间（期间新到的能并进同一段）；
 *  - `menu`＝玩家主动点入口，末尾挂功能菜单；
 *  - `lines`＝外部宿主借这张卡的一段（送达链、决策页剧情演出档），入队时台词与回调就钉死。
 */
type Segment =
    | { kind: 'backlog' }
    | { kind: 'menu' }
    | { kind: 'lines'; lines: DialogLine[]; onEnd: (picked: number | null) => void };

export class AssistPanel {
    readonly element: HTMLElement;

    private readonly dialog = new VnDialog();
    private readonly career = new CareerRecordPanel();
    private readonly endorse = new EndorseListPanel();
    private readonly reviews = new ReviewPanel();
    /** 「造型工作室」＝整页面板（不是本面板持有的弹层）⇒ 由 app 注入切页动作 */
    openStudio: (() => void) | null = null;
    /** 右下角悬浮入口：对话框或功能弹窗展开时让位 */
    private fab: HTMLElement | null = null;
    /** 月度执行行程期间抑制弹出（hideVn 置真，resumeVn 置假） */
    private suppressed = false;
    /** 待放队列（单张卡只有一个「说完交给谁」的位置，两段同时开口＝后一段吃掉前一段没兑现的收口） */
    private readonly queue: Segment[] = [];
    /** 当前正在放的那一段（卡忙的唯一尺）：`hideVn()` 打断它时原样放回队首，恢复后从头重播（宁可重复不可漏话） */
    private current: Segment | null = null;
    /** 玩家在纸卡上点选的功能：onPick 只记账，等纸卡收起后再执行，⛔ 两层浮层同帧抢屏 */
    private picked: string | null = null;
    /** 本次开菜单可见的那几项（`visibleMenu()` 摇出的快照，onPick 的下标只对它有效；工作室未开时里面⛔ 有 studio） */
    private menuItems: { key: string; label: string; reply?: string }[] = [];

    constructor() {
        this.element = this.dialog.element;
        this.dialog.onOpenChanged = (open) => this.fab?.classList.toggle('vn-hidden', open);
        this.career.onOpenChanged = (open) => this.fab?.classList.toggle('pop-hidden', open);
        this.endorse.onOpenChanged = (open) => this.fab?.classList.toggle('pop-hidden', open);
        this.reviews.onOpenChanged = (open) => this.fab?.classList.toggle('pop-hidden', open);
    }

    /** 进入游戏会话时挂载：订阅队列变化，把上次遗留的积压播掉 */
    initForGame(host: HTMLElement, fab?: HTMLElement): void {
        host.appendChild(this.element);
        host.appendChild(this.career.element);
        host.appendChild(this.endorse.element);
        host.appendChild(this.reviews.element);
        this.fab = fab ?? null;
        this.suppressed = false;
        this.picked = null;
        this.current = null;
        this.queue.length = 0;
        this.afterBacklog = null;
        EventBus.on(GameEvents.ASSISTANT_QUEUED, this.onQueued);
        this.playUnread();
    }

    /** 事件回调（不吃 emit 的载荷，免得载荷被当成菜单）：有积压就登记一段，开口时机交给 pump */
    private onQueued = (): void => this.playUnread();

    /** 悬浮入口：积压先播完，然后给功能菜单（菜单只在这条路径上出现） */
    openPanel(): void {
        if (this.suppressed) return;
        if (this.queue.some(s => s.kind === 'menu')) return;   // 已经排了一份菜单，不再叠第二份
        this.queue.push({ kind: 'menu' });
        this.pump();
    }

    /** 月度执行弹窗接管屏幕：整段收起且不记已读（resumeVn 时从头连播），实绩弹窗一并关掉 */
    hideVn(): void {
        this.suppressed = true;
        // 被打断的那段原样放回队首：恢复时从头重播，「整段读完才记已读」这条不破
        if (this.current) { this.queue.unshift(this.current); this.current = null; }
        this.picked = null;
        this.career.close();
        this.endorse.close();
        this.reviews.close();
        this.dialog.stop();
    }

    /**
     * 回到日常页：**只解除抑制，不开口**。播报时点由宿主链决定（决策页入场链 ④＝热度榜关掉之后），
     * ⛔ 在这里提前 playUnread——那会先上屏、再被入场链的 hideVn 压掉（2026-10-31 抢跑修复）。
     */
    releaseVn(): void {
        this.suppressed = false;
    }

    /** 覆盖层关闭：解除抑制并把积压连播（榜单/咖位等关窗后接回对话） */
    resumeVn(): void {
        this.suppressed = false;
        this.playUnread();
    }

    /** 退出游戏（返回主菜单 / 周目结束）：卸载事件、收起、清空队列 */
    destroy(): void {
        EventBus.off(GameEvents.ASSISTANT_QUEUED, this.onQueued);
        this.picked = null;
        this.current = null;
        this.queue.length = 0;
        this.afterBacklog = null;      // 周目结束，续行动作（开月度执行弹窗之类）不再补一刀
        this.fab = null;
        this.career.close();
        this.reviews.close();
        this.dialog.stop();
    }

    /** 自动播报：登记一段积压，开口时机交给 pump（不带菜单） */
    private playUnread = (): void => {
        this.enqueueBacklog();
        this.pump();
    };

    /**
     * 积压在队列里只占一格：排队期间又来的消息不另起一段，
     * 开口那一刻现取未读区间（见 `playSegment`），于是天然并进来，也不会堆出十段重复播报。
     */
    private enqueueBacklog(): void {
        if (this.current?.kind === 'backlog') return;
        if (!this.queue.some(s => s.kind === 'backlog')) this.queue.push({ kind: 'backlog' });
    }

    /**
     * 外部宿主（决策页剧情演出档、经纪人送达链）借用这张卡播一段对话——复用同一实例，
     * 位置/样式与平时决策页小助理对话框完全一致（2026-10-14 用户令「调用和别的决策页一样的对话框组件」）。
     * 2026-11-02 决策页软锁整改：入队而不是直接 `dialog.play`。这张卡只有一个「说完交给谁」的位置，
     * 插队会把正在播的那段的收口回调吃掉，面板永远开不了。后到的排在后面，按顺序一段段说完。
     */
    playLines(lines: DialogLine[], onEnd: (picked: number | null) => void): void {
        this.queue.push({ kind: 'lines', lines, onEnd });
        this.pump();
    }

    /** `flushThen` 排队的续行动作：整段积压播完才执行一次 */
    private afterBacklog: (() => void) | null = null;

    /**
     * 「先把小助理的话讲完，再交给下一步」。
     * 存在的理由：月度执行弹窗属 `MODAL_PANELS`，一开就会 `hideVn()` 把播报整段收起且不记已读
     * ⇒ 月初刚公布的提名播报会被挤到"执行完行程"之后才重播。2026-09-30 抢屏修复：
     * 决策日点「确认行程并进入下月」时先走这里，播完（含播出期间新到的）才开弹窗。
     */
    flushThen(done: () => void): void {
        if (this.suppressed || assistantSystem.status().unread === 0) { done(); return; }
        this.afterBacklog = done;
        this.enqueueBacklog();
        this.pump();
    }

    /**
     * 单张卡一次只放一段：卡忙就等收口回调再泵，后到的排队（2026-11-02 决策页软锁整改）。
     * 抑制期（月度执行接管屏幕）只放行外部宿主借的那段——自动播报仍压着，等 flushThen／resumeVn 再开口；
     * 借用段不等抑制，否则决策页剧情档会被积压挡死（10-31 抢跑那条令的原意只管自动播报）。
     */
    private pump(): void {
        if (this.current) return;
        const idx = this.queue.findIndex(s => s.kind === 'lines' || !this.suppressed);
        if (idx < 0) { this.settleAfterBacklog(); return; }
        this.playSegment(this.queue.splice(idx, 1)[0]);
    }

    /** 队列排空且积压读完，兑现 flushThen 存的续行动作（只兑现一次） */
    private settleAfterBacklog(): void {
        if (this.current || this.queue.length || assistantSystem.status().unread > 0) return;
        const after = this.afterBacklog;
        this.afterBacklog = null;
        after?.();
    }

    /**
     * 放一段上屏（调用前 pump 已保证卡是空的）。
     * `lines`＝借用段，台词与收口在入队那一刻钉死；`backlog`／`menu`＝小助理自己开口，
     * 未读区间在开口这一刻现取（排队期间新到的自然并进同一段）。
     */
    private playSegment(seg: Segment): void {
        if (seg.kind === 'lines') { this.playBorrowed(seg); return; }
        const withMenu = seg.kind === 'menu';
        const st = assistantSystem.status();
        const msgs: DialogLine[] = [];
        for (let i = st.readCount; i < st.total; i++) {
            const msg = assistantSystem.at(i);
            if (msg) msgs.push({ speaker: SPEAKER, text: msg.text });
        }
        if (!msgs.length && !withMenu) { this.pump(); return; }
        // 摇一次存下来：下面的 onPick 拿显示下标回查的就是这一份（工作室未开时它⛔ 在册，清单少一项）
        this.menuItems = withMenu ? visibleMenu() : [];
        const lines = withMenu ? [...msgs, menuLine(this.menuItems, this.onPick)] : msgs;
        this.current = seg;
        this.picked = null;
        this.dialog.play(lines, () => {
            // 读完整段才计已读：中途被月度执行打断则整段重播，宁可重复不可漏话
            // （菜单那句是现编的、不在队列里，故只按 msgs 条数计）
            if (msgs.length) assistantSystem.markRead(msgs.length);
            this.current = null;
            const key = this.picked;
            this.picked = null;
            if (key) { this.run(key); }
            // 播出期间新到的消息紧跟着播（自动档，不带菜单），插到队首保证仍在玩家点的菜单前面
            if (assistantSystem.status().unread > 0) this.queue.unshift({ kind: 'backlog' });
            this.pump();
        });
    }

    /** 借用段（送达链、决策页剧情演出档）：空台词不上屏也要交回收口，否则调用方的链断在半路 */
    private playBorrowed(seg: Extract<Segment, { kind: 'lines' }>): void {
        if (!seg.lines.length) {
            seg.onEnd(null);
            this.pump();
            return;
        }
        this.current = seg;
        this.dialog.play(seg.lines, (picked) => {
            this.current = null;
            seg.onEnd(picked);
            this.pump();
        });
    }

    private onPick = (index: number): void => {
        // ⚠️ 下标来自**显示中的那几项**（`this.menuItems`），⛔ 回查静态 `MENU`：工作室未开时清单少一项，
        // 用 `MENU[index]` 会把「网友评价」读成「造型工作室」（串到下一个功能上）。
        this.picked = this.menuItems[index]?.key ?? null;
    };

    /** 执行玩家点选的功能 */
    private run(key: string): void {
        if (key === 'career') { this.career.open(); return; }
        if (key === 'endorse') { this.endorse.open(); return; }
        if (key === 'reviews') {
            // Lv.1＝菜单里那句 reply 已经回过了（`visibleMenu` 现挂），这里⛔ 再开面板——同一件事⛔ 做两遍
            if (!reviewSystem.unlocked()) return;
            this.reviews.open();
            return;
        }
        if (key === 'studio') {
            // 门控已前移到清单（未开时这项压根⛔ 出现，见 visibleMenu）⇒ 走到这里就是开着的，⛔ 再判一次门槛
            this.openStudio?.();
            return;
        }
        if (key === 'news') {
            // 未上线期间＝菜单项自带 `reply`（业内资讯那句「近期没有业内资讯。」在纸卡里原位播过），这里⛔ 再做事，
            // ⛔ 补一句 toast——同一件事说两遍，且气泡⛔「小助理回复」的形制
            return;
        }
        showHint(TODO_HINT);
    }
}

/** 菜单那句台词：称呼 + 引导语，末句挂分支选项（VnDialog 的选项只在整段末句出现）
 *  items 必须就是调用方存下来给 onPick 回查的那一份（同一份快照，下标才对得上）。 */
function menuLine(items: { key: string; label: string; reply?: string }[], onPick: (index: number) => void): DialogLine {
    // 带 `reply` 的项＝选了不当场开面板，小助理原位回一句（VnDialog 现成的「选项→反应句」连播，人脉批 C 同款）
    const choices: DialogChoice[] = items.map(m => ({
        text: m.label,
        reply: m.reply ? { speaker: SPEAKER, text: m.reply } : undefined,
    }));
    return {
        speaker: SPEAKER,
        text: assistantSystem.joinTerm(ASSISTANT.menuHint),
        choices,
        onPick,
    };
}
